import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { SEO, breadcrumbSchema } from '../components/SEO';
import { STATIC_SEO } from '../data/seoConfig';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import EditorialMarquee from '../components/EditorialMarquee';
import { Reveal } from '../components/Reveal';
import { SITE, IMAGES } from '../data/site';

const VALUES = [
  ['Quality Craftsmanship', 'Meticulous attention to detail and premium materials in every project.'],
  ['Custom Design', 'Tailored solutions that reflect your unique style and functional needs — never one-size-fits-all.'],
  ['Timely Completion', 'Efficient project management ensuring on-time delivery without compromising quality.'],
  ['Local Expertise', 'Deep understanding of Nebraska\'s climate and local building requirements.'],
];

const AboutPage = () => (
  <>
    <SEO
      title={STATIC_SEO.about.title}
      description={STATIC_SEO.about.description}
      path="/about/"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about/' }])]}
    />
    <PageHero
      overline="About Us"
      title="Your local outdoor living experts"
      intro="Transforming outdoor spaces across Nebraska with expert craftsmanship, innovative design, and an unwavering commitment to quality."
      image={IMAGES.homeDusk}
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'About' }]}
    />

    <section className="py-20 md:py-28">
      <div className="container-site grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
              {SITE.years} years of excellence. Locally owned. {SITE.rating}.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted2 md:text-lg">
              {SITE.name} ({SITE.legalName}) is a locally-owned and operated company with over {SITE.years} years of proven experience in the outdoor living industry. Dedicated to transforming backyards across Omaha and Lincoln into stunning outdoor living spaces, we've earned a reputation as the trusted partner for premium outdoor construction and design through decades of hands-on expertise and unwavering commitment to customer satisfaction.
            </p>
            <p className="mt-5 text-base leading-relaxed text-muted2 md:text-lg">
              From custom outdoor kitchens and elegant sunrooms to beautiful decks, pergolas, and water &amp; fire features, we specialize in creating personalized outdoor sanctuaries that enhance your home's value and your family's quality of life. Every project is designed and built with meticulous attention to detail and premium craftsmanship.
            </p>
            <p className="mt-5 text-base leading-relaxed text-muted2 md:text-lg">
              We believe in building lasting relationships with our clients through transparent communication, honest pricing, and exceptional results. Your satisfaction is our success, and we're committed to exceeding your expectations on every project.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="img-frame aspect-[4/5] border border-line p-3">
              <img src={IMAGES.pergola.src} alt={IMAGES.pergola.alt} loading="lazy" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <EditorialMarquee />

    <section className="py-20 md:py-28">
      <div className="container-site">
        <Reveal>
          <p className="overline-label">Why Choose Us</p>
          <h2 className="mt-3 max-w-2xl font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">Our commitment to excellence sets us apart</h2>
        </Reveal>
        <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(([title, text], i) => (
            <div key={title} className="bg-paper p-8">
              <Reveal delay={i * 0.08}>
                <span className="font-serif text-4xl font-light italic text-bronze">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-4 font-serif text-xl font-medium text-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted2">{text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-line bg-paper2/50 py-20 md:py-28">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">Serving Omaha &amp; Lincoln areas</h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-muted2">
            We proudly serve homeowners throughout the greater Omaha and Lincoln metropolitan areas, bringing our expertise in outdoor living solutions directly to your doorstep.
          </p>
          <ul className="mt-8 space-y-3.5">
            {['Greater Omaha Metropolitan Area', 'Lincoln and surrounding communities', 'Free on-site consultations', 'Licensed & insured'].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm font-medium text-ink/80">
                <Check className="h-4 w-4 text-bronze" /> {item}
              </li>
            ))}
          </ul>
          <Link to="/service-areas/" data-testid="about-areas-link" className="btn-outline mt-9">Explore Service Areas</Link>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="img-frame aspect-[4/3] border border-line bg-paper p-3">
            <img src={IMAGES.homeModern.src} alt={IMAGES.homeModern.alt} loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>

    <CTASection title="Ready to start your outdoor living project?" text="Let's discuss your vision and create a custom outdoor living solution that transforms your backyard into your personal oasis." testId="about-cta" />
  </>
);

export default AboutPage;
